import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Conversa, MedicoRecado, Recado } from '../../../interfaces/recados.dto';
import { RecadosService } from '../../../services/recados/recados.service';
import { RecadosHubService } from '../../../services/recados/recados-hub.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { agruparConversas, chaveLocal, mesclarRecados, tamanhoCodificado } from '../../../utils/recados.util';
import { salvarBlob } from '../../../utils/baixar-arquivo.util';

type Aba = 'todas' | 'nao-lidas' | 'favoritos' | 'arquivadas';
type Modo = 'lista' | 'conversa' | 'nova';

interface AnexoPendente { dataUrl: string; nome: string; tipo: string; }

const LIMITE_TEXTO = 2000;
// O contador só aparece perto do limite.
const AVISO_TEXTO = 1800;
const LIMITE_ANEXO = 5 * 1024 * 1024;
const MARCADOR_ANEXO = '__img__';

// Mesma lista do back.
const TIPOS_ACEITOS = new Set([
  'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain', 'application/zip',
  'application/x-zip-compressed', 'application/vnd.rar', 'application/x-rar-compressed', 'text/csv',
]);

// No Windows .rar e .csv chegam sem File.type.
const TIPO_POR_EXTENSAO: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', pdf: 'application/pdf',
  doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain', zip: 'application/zip', rar: 'application/vnd.rar', csv: 'text/csv',
};

const EXTENSAO_POR_TIPO: Record<string, string> = Object.fromEntries(
  Object.entries(TIPO_POR_EXTENSAO).filter(([ext]) => ext !== 'jpeg').map(([ext, tipo]) => [tipo, ext]),
);

const normalizar = (s: string | null | undefined) => (s ?? '').trim().toUpperCase();

@Component({
  selector: 'app-chat-medico-painel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat-medico-painel.component.html',
  styleUrl: './chat-medico-painel.component.scss',
})
export class ChatMedicoPainelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() aberto = false;
  @Output() fechar = new EventEmitter<void>();
  @Output() naoLidasMudou = new EventEmitter<number>();
  @ViewChild('caixa') private caixa?: ElementRef<HTMLTextAreaElement>;

  readonly limiteTexto = LIMITE_TEXTO;
  readonly abas: { id: Aba; rotulo: string }[] = [
    { id: 'todas', rotulo: 'Todas' }, { id: 'nao-lidas', rotulo: 'Não lidas' },
    { id: 'favoritos', rotulo: 'Favoritos' }, { id: 'arquivadas', rotulo: 'Arquivadas' },
  ];

  modo: Modo = 'lista';
  aba: Aba = 'todas';
  busca = '';

  eu = '';
  recados: Recado[] = [];
  conversas: Conversa[] = [];
  visiveis: Conversa[] = [];
  mensagens: Recado[] = [];
  carregando = false;
  erroLista = '';
  private carregado = false;
  // Eventos que chegam durante o listar(); sem isso a lista nova os apagaria.
  private recebidosNaCarga: Recado[] | null = null;
  private ultimoTotal: number | null = null;

  aberta = '';
  nomeAberta = '';

  medicos: MedicoRecado[] | null = null;
  carregandoMedicos = false;
  erroMedicos = '';
  buscaMedico = '';

  menuAberto = false;

  texto = '';
  anexoPendente: AnexoPendente | null = null;
  enviando = false;
  erro = '';

  private anexos = new Map<number, string>();
  private visiveisAnexo = new Set<number>();
  private baixando = new Set<number>();
  private emExclusao = new Set<number>();

  private favoritos = new Set<string>();
  private arquivadas = new Set<string>();
  private login = '';
  private sub?: Subscription;

  constructor(
    private recadosService: RecadosService,
    private hub: RecadosHubService,
    private localStorage: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.login = this.localStorage.get('login') ?? '';
    this.favoritos = this.lerConjunto('favoritos');
    this.arquivadas = this.lerConjunto('arquivadas');
    this.sub = this.hub.novo$.subscribe(r => this.receber(r));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['aberto'] && this.aberto) this.carregar();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  @HostListener('document:keydown.escape', ['$event'])
  tecla(e: KeyboardEvent): void {
    if (!this.aberto || e.defaultPrevented) return;
    if (this.menuAberto) { this.menuAberto = false; return; }
    this.fechar.emit();
  }

  async carregar(): Promise<void> {
    this.carregando = true;
    this.erroLista = '';
    this.recebidosNaCarga = [];
    try {
      const [eu, lista] = await Promise.all([this.eu ? Promise.resolve(this.eu) : this.recadosService.eu(), this.recadosService.listar()]);
      this.eu = eu;
      this.recados = mesclarRecados(lista, this.recebidosNaCarga ?? []);
      this.carregado = true;
      if (this.modo === 'conversa') this.marcarLidas();
      this.atualizar();
    } catch (e: any) {
      this.erroLista = e?.message ?? 'Não foi possível carregar os recados agora.';
    } finally {
      this.recebidosNaCarga = null;
      this.carregando = false;
    }
  }

  // --- lista ---

  escolherAba(aba: Aba): void {
    this.aba = aba;
    this.filtrar();
  }

  filtrar(): void {
    const t = normalizar(this.busca);
    this.visiveis = this.conversas
      .filter(c => !t || normalizar(c.outro).includes(t))
      .filter(c => {
        const k = normalizar(c.outro);
        if (this.aba === 'arquivadas') return this.arquivadas.has(k);
        if (this.arquivadas.has(k)) return false;
        if (this.aba === 'nao-lidas') return c.naoLidas > 0;
        if (this.aba === 'favoritos') return this.favoritos.has(k);
        return true;
      })
      .sort((a, b) => Number(this.ehFavorito(b.outro)) - Number(this.ehFavorito(a.outro)));
  }

  ehFavorito(nome: string): boolean { return this.favoritos.has(normalizar(nome)); }
  ehArquivada(nome: string): boolean { return this.arquivadas.has(normalizar(nome)); }

  alternarFavorito(): void { this.alternar(this.favoritos, 'favoritos'); }
  alternarArquivada(): void { this.alternar(this.arquivadas, 'arquivadas'); }

  private alternar(conjunto: Set<string>, tipo: 'favoritos' | 'arquivadas'): void {
    this.menuAberto = false;
    conjunto.has(this.aberta) ? conjunto.delete(this.aberta) : conjunto.add(this.aberta);
    try {
      this.localStorage.set(chaveLocal(tipo, this.login), [...conjunto]);
    } catch {}
    this.filtrar();
  }

  private lerConjunto(tipo: 'favoritos' | 'arquivadas'): Set<string> {
    try {
      const v = this.localStorage.get(chaveLocal(tipo, this.login));
      return new Set(Array.isArray(v) ? v : []);
    } catch {
      return new Set();
    }
  }

  // --- conversa ---

  abrirConversa(nome: string): void {
    this.aberta = normalizar(nome);
    this.nomeAberta = nome.trim();
    this.modo = 'conversa';
    this.menuAberto = false;
    this.limparComposicao();
    this.marcarLidas();
    this.atualizar();
  }

  voltar(): void {
    this.modo = 'lista';
    this.aberta = '';
    this.nomeAberta = '';
    this.menuAberto = false;
    this.limparComposicao();
  }

  especialidade(nome: string): string {
    const k = normalizar(nome);
    return this.medicos?.find(m => normalizar(m.nmMedico) === k)?.dsEspecialidade ?? '';
  }

  ehMinha(r: Recado): boolean { return normalizar(r.nmRemetente) === normalizar(this.eu); }

  hora(r: Recado): string { return (r.dtCriacaoFmt ?? '').split(' ')[1] ?? ''; }
  data(r: Recado): string { return (r.dtCriacaoFmt ?? '').split(' ')[0] ?? ''; }

  /** Hora se for de hoje; senão dia/mês. */
  quando(r: Recado): string {
    const d = this.data(r);
    const h = new Date();
    const hoje = `${String(h.getDate()).padStart(2, '0')}/${String(h.getMonth() + 1).padStart(2, '0')}/${h.getFullYear()}`;
    return d === hoje ? this.hora(r) : d.slice(0, 5);
  }

  previa(c: Conversa): string {
    const u = c.ultima;
    if (u.dsMensagem) return u.dsMensagem;
    return u.dsImagem === MARCADOR_ANEXO ? (this.ehImagem(u) ? 'Imagem' : u.dsArquivoNome || 'Arquivo') : '';
  }

  iniciais(nome: string): string {
    const p = nome.replace(/^(DRA?\.?)\s+/i, '').split(/\s+/).filter(Boolean);
    return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '?';
  }

  private marcarLidas(): void {
    const meu = normalizar(this.eu);
    this.recados = this.recados.map(r => {
      if (r.ieLido !== 'N' || normalizar(r.nmRemetente) !== this.aberta || normalizar(r.nmDestinatario) !== meu) return r;
      this.recadosService.marcarLido(r.nrSequencia).catch(() => {});
      return { ...r, ieLido: 'S' as const };
    });
  }

  private receber(novo: Recado): void {
    // Conexão aberta por outro login na mesma aba (sessão expirada sem passar pelo logout).
    const meu = normalizar(this.eu);
    if (meu && normalizar(novo.nmRemetente) !== meu && normalizar(novo.nmDestinatario) !== meu) return;
    const atual = this.recados.find(r => r.nrSequencia === novo.nrSequencia);
    // Lido não volta atrás: o mesmo evento pode chegar depois de marcarmos.
    if (atual?.ieLido === 'S') novo = { ...novo, ieLido: 'S' };
    this.recebidosNaCarga?.push(novo);
    this.recados = mesclarRecados(this.recados, [novo]);
    // Com o painel fechado ninguém está lendo: não marca.
    if (this.aberto && this.modo === 'conversa' && normalizar(novo.nmRemetente) === this.aberta) this.marcarLidas();
    this.atualizar();
  }

  private atualizar(): void {
    this.conversas = agruparConversas(this.recados, this.eu);
    const c = this.conversas.find(x => normalizar(x.outro) === this.aberta);
    this.mensagens = c ? [...c.recados].reverse() : [];
    this.filtrar();
    if (!this.carregado) return;
    const total = this.conversas.reduce((s, x) => s + x.naoLidas, 0);
    if (total !== this.ultimoTotal) {
      this.ultimoTotal = total;
      this.naoLidasMudou.emit(total);
    }
  }

  excluindo(r: Recado): boolean { return this.emExclusao.has(r.nrSequencia); }

  async excluir(r: Recado): Promise<void> {
    if (this.emExclusao.has(r.nrSequencia)) return;
    this.emExclusao.add(r.nrSequencia);
    this.erro = '';
    try {
      await this.recadosService.excluir(r.nrSequencia);
      this.recados = this.recados.filter(x => x.nrSequencia !== r.nrSequencia);
      this.anexos.delete(r.nrSequencia);
      this.atualizar();
    } catch (e: any) {
      this.erro = e?.message ?? 'Não foi possível excluir o recado.';
    } finally {
      this.emExclusao.delete(r.nrSequencia);
    }
  }

  // --- nova mensagem ---

  async novaMensagem(): Promise<void> {
    this.modo = 'nova';
    this.aberta = '';
    this.buscaMedico = '';
    this.menuAberto = false;
    this.limparComposicao();
    if (this.medicos || this.carregandoMedicos) return;
    this.carregandoMedicos = true;
    this.erroMedicos = '';
    try {
      this.medicos = await this.recadosService.medicos();
    } catch (e: any) {
      this.erroMedicos = e?.message ?? 'Não foi possível carregar os médicos agora.';
    } finally {
      this.carregandoMedicos = false;
    }
  }

  get medicosVisiveis(): MedicoRecado[] {
    const t = semAcento(this.buscaMedico);
    if (!t) return this.medicos ?? [];
    return (this.medicos ?? []).filter(m => semAcento(m.nmMedico).includes(t) || semAcento(m.dsEspecialidade ?? '').includes(t));
  }

  escolherMedico(m: MedicoRecado): void { this.abrirConversa(m.nmMedico); }

  porMedico(_: number, m: MedicoRecado): string { return m.nmUsuario; }

  // --- composição ---

  get tamanho(): number { return tamanhoCodificado(this.texto); }
  get passouLimite(): boolean { return this.tamanho > LIMITE_TEXTO; }
  get mostrarContador(): boolean { return this.tamanho >= AVISO_TEXTO; }

  ajustarAltura(ta: HTMLTextAreaElement): void {
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }

  teclaTexto(e: KeyboardEvent): void {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.enviar();
    }
  }

  colar(e: ClipboardEvent): void {
    const item = Array.from(e.clipboardData?.items ?? []).find(i => i.kind === 'file' && i.type.startsWith('image/'));
    const arquivo = item?.getAsFile();
    if (!arquivo) return;
    e.preventDefault();
    this.anexar(arquivo);
  }

  escolherArquivo(e: Event): void {
    const input = e.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    input.value = '';
    if (arquivo) this.anexar(arquivo);
  }

  async anexar(arquivo: File): Promise<void> {
    this.erro = '';
    this.anexoPendente = null;
    if (arquivo.size > LIMITE_ANEXO) {
      this.erro = 'O arquivo passa de 5 MB.';
      return;
    }
    // Navegadores dão tipos alternativos (ex.: application/x-rar); a extensão decide nesses casos.
    const tipo = TIPOS_ACEITOS.has(arquivo.type) ? arquivo.type : TIPO_POR_EXTENSAO[(arquivo.name.split('.').pop() ?? '').toLowerCase()] ?? '';
    if (!TIPOS_ACEITOS.has(tipo)) {
      this.erro = 'Tipo de arquivo não aceito.';
      return;
    }
    try {
      // O back exige o mesmo tipo no prefixo; sem File.type o leitor põe application/octet-stream.
      const dataUrl = (await lerComoDataUrl(arquivo)).replace(/^data:[^;,]*;base64,/, `data:${tipo};base64,`);
      this.anexoPendente = { dataUrl, nome: arquivo.name || 'imagem.png', tipo };
    } catch {
      this.erro = 'Não foi possível ler o arquivo.';
    }
  }

  removerAnexo(): void { this.anexoPendente = null; }

  async enviar(): Promise<void> {
    if (this.enviando || this.passouLimite) return;
    const destinatario = this.nomeAberta;
    if (!destinatario) return;
    const mensagem = this.texto.trim();
    if (!mensagem && !this.anexoPendente) { this.erro = 'Escreva uma mensagem ou anexe um arquivo.'; return; }
    this.enviando = true;
    this.erro = '';
    const anexo = this.anexoPendente;
    try {
      const enviado = await this.recadosService.enviar({
        destinatario,
        ...(mensagem ? { mensagem } : {}),
        ...(anexo ? { imagem: anexo.dataUrl, arquivoNome: anexo.nome, arquivoTipo: anexo.tipo } : {}),
      });
      if (enviado) {
        if (anexo) this.anexos.set(enviado.nrSequencia, anexo.dataUrl);
        this.recados = mesclarRecados(this.recados, [enviado]);
      }
      this.abrirConversa(destinatario);
    } catch (e: any) {
      this.erro = e?.message ?? 'Não foi possível enviar agora. Tente de novo.';
    } finally {
      this.enviando = false;
    }
  }

  private limparComposicao(): void {
    this.texto = '';
    if (this.caixa) this.caixa.nativeElement.style.height = '';
    this.anexoPendente = null;
    this.erro = '';
  }

  // --- anexos recebidos ---

  temAnexo(r: Recado): boolean { return r.dsImagem === MARCADOR_ANEXO; }
  // Agenda e pav gravam imagem colada sem DS_ARQUIVO_TIPO.
  ehImagem(r: Recado): boolean { return !r.dsArquivoTipo || r.dsArquivoTipo.startsWith('image/'); }
  imagemVisivel(r: Recado): boolean { return this.visiveisAnexo.has(r.nrSequencia) && this.anexos.has(r.nrSequencia); }
  srcAnexo(r: Recado): string { return this.anexos.get(r.nrSequencia) ?? ''; }
  baixandoAnexo(r: Recado): boolean { return this.baixando.has(r.nrSequencia); }

  async alternarImagem(r: Recado): Promise<void> {
    if (this.visiveisAnexo.delete(r.nrSequencia)) return;
    if (await this.carregarAnexo(r)) this.visiveisAnexo.add(r.nrSequencia);
  }

  async baixarArquivo(r: Recado): Promise<void> {
    const dataUrl = await this.carregarAnexo(r);
    if (!dataUrl) return;
    try {
      salvarBlob(dataUrlParaBlob(dataUrl), this.nomeDoAnexo(r, dataUrl));
    } catch {
      this.erro = 'Não foi possível abrir o anexo.';
    }
  }

  nomeDoAnexo(r: Recado, dataUrl: string): string {
    if (r.dsArquivoNome) return r.dsArquivoNome;
    const ext = EXTENSAO_POR_TIPO[/^data:([^;,]+)/.exec(dataUrl)?.[1] ?? ''];
    return `anexo-${r.nrSequencia}${ext ? '.' + ext : ''}`;
  }

  private async carregarAnexo(r: Recado): Promise<string> {
    const cache = this.anexos.get(r.nrSequencia);
    if (cache) return cache;
    if (this.baixando.has(r.nrSequencia)) return '';
    this.baixando.add(r.nrSequencia);
    this.erro = '';
    try {
      const dataUrl = await this.recadosService.anexo(r.nrSequencia);
      if (dataUrl) this.anexos.set(r.nrSequencia, dataUrl);
      return dataUrl;
    } catch (e: any) {
      this.erro = e?.message ?? 'Não foi possível abrir o anexo.';
      return '';
    } finally {
      this.baixando.delete(r.nrSequencia);
    }
  }

  porNr(_: number, r: Recado): number { return r.nrSequencia; }
  porOutro(_: number, c: Conversa): string { return normalizar(c.outro); }
}

function semAcento(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

function lerComoDataUrl(arquivo: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result));
    leitor.onerror = () => reject(leitor.error);
    leitor.readAsDataURL(arquivo);
  });
}

function dataUrlParaBlob(dataUrl: string): Blob {
  const [cabecalho, corpo = ''] = dataUrl.split(',', 2);
  const tipo = /data:([^;,]+)/.exec(cabecalho)?.[1] ?? 'application/octet-stream';
  if (!/;base64/i.test(cabecalho)) return new Blob([decodeURIComponent(corpo)], { type: tipo });
  const bin = atob(corpo);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: tipo });
}
